import React, { useState, useEffect } from "react";
import { Button, Spinner, Alert } from "react-bootstrap";
import HeroSection from "./ProfileComponents/HeroSection";
import AboutSection from "./ProfileComponents/AboutSection";
import MissionVisionSection from "./ProfileComponents/MissionVisionSection";
import StrategicObjectivesSection from "./ProfileComponents/StrategicObjectivesSection";
import MagnificentPunjabSection from "./ProfileComponents/MagnificentPunjabSection";
import FuelingTourismSection from "./ProfileComponents/FuelingTourismSection";
import PolicySection from "./ProfileComponents/PolicySection";
import ServicesSection from "./ProfileComponents/ServicesSection";
import VideosSection from "./ProfileComponents/VideosSection";
import DevelopmentSchemesSection from "./ProfileComponents/DevelopmentSchemesSection";
import MetaSection from "./ProfileComponents/MetaSection";
import { sanitizeData } from ".";

const ProfileEditor = ({ data, onSave, onCancel, isLoading }) => {
  const defaultStructure = {
    heroSection: {
      title: "",
      backgroundImage: "",
      isVisible: true,
    },
    aboutSection: {
      heading: "",
      description: "",
      image: "",
      isVisible: true,
    },
    missionVisionSection: {
      heading: "",
      description: "",
      mainImage: "",
      missionImage: "",
      visionImage: "",
      isVisible: true,
    },
    strategicObjectivesSection: {
      heading: "",
      backgroundImage: "",
      objectives: [
        {
          id: "",
          title: "",
        },
      ],
      isVisible: true,
    },
    magnificentPunjabSection: {
      heading: "",
      image: "",
      economicImpacts: [
        {
          title: "",
          description: "",
        },
      ],
      isVisible: true,
    },
    fuelingTourismSection: {
      heading: "",
      description: "",
      backgroundImage: "",
      isVisible: true,
    },
    policySection: {
      heading: "",
      description: "",
      image: "",
      initiatives: [""],
      isVisible: true,
    },
    servicesSection: {
      heading: "",
      backgroundImage: "",
      services: [
        {
          id: "",
          title: "",
        },
      ],
      isVisible: true,
    },
    videosSection: {
      isVisible: true,
      videoUrls: [""],
    },
    developmentSchemesSection: {
      heading: "",
      ongoingSchemes: [
        {
          title: "",
          points: [""],
        },
      ],
      newSchemes: [
        {
          title: "",
          points: [""],
        },
      ],
      isVisible: true,
    },
    meta: {
      title: "",
      description: "",
    },
  };

  const [formData, setFormData] = useState(defaultStructure);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "info" });

  useEffect(() => {
    if (data?.content) {
      setFormData({ ...defaultStructure, ...data.content });
    }
  }, [data]);

  const handleSectionUpdate = (sectionName, sectionData) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: sectionData
    }));
  };

  const handleSave = () => {
    const sanitizedData = sanitizeData(formData, defaultStructure);
    onSave({ pageName: data.pageName, content: sanitizedData });
  };

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ show: false, message: "", variant: "info" })}
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}

      <div className="card p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Button variant="link" className="p-0 me-3" onClick={onCancel}>
              ← Back
            </Button>
            <h4 className="m-0">Edit Profile Page</h4>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <HeroSection
              data={formData.heroSection}
              onUpdate={(data) => handleSectionUpdate("heroSection", data)}
            />

            <AboutSection
              data={formData.aboutSection}
              onUpdate={(data) => handleSectionUpdate("aboutSection", data)}
            />

            <MissionVisionSection
              data={formData.missionVisionSection}
              onUpdate={(data) => handleSectionUpdate("missionVisionSection", data)}
            />

            <StrategicObjectivesSection
              data={formData.strategicObjectivesSection}
              onUpdate={(data) => handleSectionUpdate("strategicObjectivesSection", data)}
            />

            <MagnificentPunjabSection
              data={formData.magnificentPunjabSection}
              onUpdate={(data) => handleSectionUpdate("magnificentPunjabSection", data)}
            />

            <FuelingTourismSection
              data={formData.fuelingTourismSection}
              onUpdate={(data) => handleSectionUpdate("fuelingTourismSection", data)}
            />

            <PolicySection
              data={formData.policySection}
              onUpdate={(data) => handleSectionUpdate("policySection", data)}
            />

            <ServicesSection
              data={formData.servicesSection}
              onUpdate={(data) => handleSectionUpdate("servicesSection", data)}
            />

            <VideosSection
              data={formData.videosSection}
              onUpdate={(data) => handleSectionUpdate("videosSection", data)}
            />

            <DevelopmentSchemesSection
              data={formData.developmentSchemesSection}
              onUpdate={(data) => handleSectionUpdate("developmentSchemesSection", data)}
            />

            <MetaSection
              data={formData.meta}
              onUpdate={(data) => handleSectionUpdate("meta", data)}
            />

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;
